import dayjs from "dayjs";
import { useIntl } from "react-intl";

import type { Traveller } from "@/features/travellers/types/types";
import { travellerTitleMessages } from "@/features/travellers/utils/travellerTitles";
import { longDateFormatOptions } from "@/utils/dates";

const TravellerInfoRow = ({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) => (
  <div className="flex justify-between">
    <span className="text-sm font-normal text-text-tertiary">{label}</span>
    <span className="text-sm font-medium text-text-primary">
      {value ?? "-"}
    </span>
  </div>
);

type Props = {
  traveller: Traveller;
  hidePassportDetails?: boolean;
};

export const TravellerDetails = ({ traveller, hidePassportDetails }: Props) => {
  const intl = useIntl();

  return (
    <div className="space-y-3">
      <div className="font-medium text-text-primary">
        {intl.formatMessage({
          defaultMessage: "Personal information",
          description:
            "Title of personal information section of traveller details",
        })}
      </div>
      <TravellerInfoRow
        label={intl.formatMessage({
          defaultMessage: "Title",
          description: "Label for the traveller's title field",
        })}
        value={intl.formatMessage(travellerTitleMessages[traveller.title])}
      />
      <TravellerInfoRow
        label={intl.formatMessage({
          defaultMessage: "English Name",
          description: "Label for the traveller's first name field",
        })}
        value={traveller.firstName}
      />
      <TravellerInfoRow
        label={intl.formatMessage({
          defaultMessage: "English Surname",
          description: "Label for the traveller's last name / surname field",
        })}
        value={traveller.lastName}
      />
      <TravellerInfoRow
        label={intl.formatMessage({
          defaultMessage: "Email Address",
          description: "Label for the traveller's email address field",
        })}
        value={traveller.emailAddress}
      />
      <TravellerInfoRow
        label={intl.formatMessage({
          defaultMessage: "Date of Birth",
          description: "Label for the traveller's date of birth field",
        })}
        value={intl.formatDate(traveller.birthDate, longDateFormatOptions)}
      />
      <TravellerInfoRow
        label={intl.formatMessage({
          defaultMessage: "Phone number",
          description: "Label for the traveller's phone number field",
        })}
        value={traveller.phonePrefix + traveller.phoneNumber}
      />

      {!hidePassportDetails && (
        <>
          <div className="mt-1 font-medium text-text-primary">
            {intl.formatMessage({
              defaultMessage: "Passport details",
              description:
                "Title of passport details section of traveller details",
            })}
          </div>
          <TravellerInfoRow
            label={intl.formatMessage({
              defaultMessage: "Nationality",
              description: "Label for the traveller's nationality field",
            })}
            value={traveller.passportNationality}
          />
          <TravellerInfoRow
            label={intl.formatMessage({
              defaultMessage: "Passport ID",
              description: "Label for the passport number field",
            })}
            value={traveller.passportNumber}
          />
          <TravellerInfoRow
            label={intl.formatMessage({
              defaultMessage: "Issue Date",
              description: "Label for the passport issue date field",
            })}
            value={
              traveller.passportIssuingDate
                ? intl.formatDate(
                    dayjs(traveller.passportIssuingDate).toDate(),
                    longDateFormatOptions,
                  )
                : undefined
            }
          />
          <TravellerInfoRow
            label={intl.formatMessage({
              defaultMessage: "Date of expiry",
              description: "Label for the passport expiry date field",
            })}
            value={
              traveller.passportExpiryDate
                ? intl.formatDate(
                    dayjs(traveller.passportExpiryDate).toDate(),
                    longDateFormatOptions,
                  )
                : undefined
            }
          />
        </>
      )}
    </div>
  );
};
